import { debounce } from "@material-ui/core";
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import makeStyles from "@material-ui/styles/makeStyles/makeStyles";
import { matchString } from "api/common";
import { useQaId } from "lqx-react/lib/forms/qa-ids";
import React, { useEffect } from "react";
import Select, { components } from "react-select";
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import VirtualList from "react-tiny-virtual-list";

const matchStringToLower = (first: string, second: string) => first && first.toLowerCase().trim() === second.toLowerCase().trim();

/**
 * Payment Method aliases for string matching
 * Maps alternative names to the canonical payment method names
 */
const PaymentMethodAliases = {
    'wire transfer': 'Wire',
    'wire': 'Wire',
    'ach': 'ACH',
    'check': 'Check'
};

/**
 * Resolves aliases for payment method names
 * @param value The value to resolve (e.g., "Wire Transfer" -> "Wire")
 * @returns The canonical name or original value if no alias found
 */
const resolveAlias = (value: string): string => {
    if (!value) return value;
    const lowerValue = value.toLowerCase().trim();
    return PaymentMethodAliases[lowerValue] || value;
};

export const DigiSelect = ({ id = 'id', label = 'name', options, $value, styles, ...rest }: { id: string, label: string, options, $value, styles?: any }) => {
    const classes = useStyles();

    const resolveInitialId = () => {
        if (!$value.value || !$value.value[label])
            return null;

        // Get the input value and resolve any aliases
        const inputValue = $value.value[label];
        const resolvedValue = resolveAlias(inputValue);

        // Find option by string matching (with alias resolution)
        const optionByLabel = options.filter(option => 
            matchStringToLower(option[label], resolvedValue) ||
            matchStringToLower(option[label], inputValue)
        );
        
        if (optionByLabel.length > 0) {
            return optionByLabel[0][id];
        }
        
        return null;
    }

    const filterOption = (option, searchText) => {
        return !searchText || matchString(option.label, searchText);
    };

    useEffect(() => {
        const initialId = resolveInitialId();
        var initialOption = options.filter(option => option[id] === initialId);
        $value.set(initialOption.length > 0 ? initialOption[0] : {});
    }, [$value.value ? $value.value['_changeToken'] : true]);

    const dataQaId = useQaId(rest['data-qa-id'] || label || 'digi-select');

    return (
        <Select {...{ id, label }}
            aria-label={ label }
            components={{ DropdownIndicator: CustomDropdownIndicator, MenuList: CustomMenuList }}
            getOptionLabel={option => option[label]}
            getOptionValue={option => option[id]}
            formatOptionLabel={option => (
                <div title={option[label]}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} >{option[label]}</div>
            )}
            inputId={dataQaId}
            styles={{ ...customStyles, ...styles }}
            menuPortalTarget={document.body}
            placeholder=""
            className={classes.selectionBox}
            options={options}
            value={$value.value}
            onChange={(option) => {
                $value.set(option);
            }}
            filterOption={filterOption}
            captureMenuScroll={false}
        />
    )
}

export const DigiAsyncSelect = ({ getItems, $value, isMulti, isCreater, isAgGridNested, ...rest }: { getItems: any, $value: any, isMulti?: boolean, isCreater?: boolean, isAgGridNested?: boolean }) => {
    const getLoadOptions = async (query, callback) => {
        const response = await getItems(query);
        callback(response.items || response);
    };
    const loadOptions = debounce(getLoadOptions, 300);

    const dataQaId = useQaId(rest['data-qa-id'] || 'digi-select');
    const ariaLabel: string = rest['aria-label'] || dataQaId;

    return isCreater ?
        <AsyncCreatableSelect
            aria-label={ ariaLabel }
            styles={isAgGridNested ? dropdownForAgGridStyles : customStyles}
            cacheOptions
            defaultOptions
            value={$value.value}
            getOptionLabel={(e: any) => {
                if (e.name || e.value)
                    return e.name ? e.name : `Set value: "${e.value}"`
            }}
            getOptionValue={(e: any) => e.id}
            loadOptions={loadOptions}
            onChange={(value: any) =>
                $value.set(value.value ? { label: value.label, name: value.value } : value)}
        /> :
        <AsyncSelect
            aria-label={ ariaLabel }
            isMulti={isMulti}
            styles={isAgGridNested ? dropdownForAgGridStyles :customStyles}
            cacheOptions
            defaultOptions
            isClearable={$value && $value.value.$.name.length > 0}
            value={$value.value}
            getOptionLabel={(e: any) => e.name}
            getOptionValue={(e: any) => e.id}
            loadOptions={loadOptions}
            onChange={(value: any) => $value.set(value)}
        />
}

const useStyles = makeStyles({
    selectionBox: {
        fontSize: 12,
        width: 'auto',
        height: '30px',
        marginBottom: '15px',
        float: 'left',
    },
    menuList: {
        fontSize: 12
    }
});

export const DigitizationSelect = ({ $value, getItems, options, isMulti, placeholder, isDisabled, ...rest }: { $value: any, getItems?: any, options?: any[], isMulti, placeholder?: string, isDisabled?: boolean }) => {
    const getLoadOptions = async (query, callback) => {
        const response = await getItems(query);
        callback(response.items);
    };
    const loadOptions = debounce(getLoadOptions, 300);

    const dataQaId = useQaId(rest['data-qa-id'] || 'digi-select');
    const ariaLabel: string = rest['aria-label'] || dataQaId;

    return getItems ?
        <AsyncSelect
            aria-label={ ariaLabel }
            isMulti={isMulti}
            styles={customStyles}
            placeholder={placeholder}
            cacheOptions
            defaultOptions
            value={$value.value.models || $value.value}
            getOptionLabel={(e: any) => e.name}
            getOptionValue={(e: any) => e.id}
            loadOptions={loadOptions}
            onChange={(value: any) => $value.set(value || [])}
            isDisabled={isDisabled}
        /> :
        <Select
            aria-label={ ariaLabel }
            isMulti={isMulti}
            styles={customStyles}
            placeholder={placeholder}
            options={options}
            getOptionLabel={(e: any) => e.name}
            value={$value.value}
            getOptionValue={(e: any) => e.id}
            isOptionDisabled={(e: any) => e.disabled}
            onChange={(value: any) => $value.set(value)}
            isDisabled={isDisabled}
        />
}

const CustomDropdownIndicator = props => {
    return (components.DropdownIndicator &&
        <div>
            <components.DropdownIndicator {...props}>
                <ArrowDropDownIcon />
            </components.DropdownIndicator>
        </div>
    );
};

const DefaultItemHeight = 40;

const CustomMenuList = ({ options, children, maxHeight, getValue }) => {
    const [value] = getValue();
    const initialOffset = options.indexOf(value) * DefaultItemHeight;
    const childrenOptions = React.Children.toArray(children);
    const wrapperHeight = maxHeight < childrenOptions.length * DefaultItemHeight ?
        maxHeight : childrenOptions.length * DefaultItemHeight;
    const classes = useStyles();

    return (
        <VirtualList
            className={classes.menuList}
            width="100%"
            height={wrapperHeight + 6}
            scrollOffset={initialOffset}
            itemCount={childrenOptions.length}
            itemSize={DefaultItemHeight}
            renderItem={({ style, index }) => {
                if (Array.isArray(children)) {
                    return (
                        <span style={style} key={index}>
                            {children[index]}
                        </span>
                    );
                }
                return (
                    <span key={index}>
                        {children.props.children}
                    </span>
                );
            }}
        />
    );
}

export const DigiCurrencySelect = ({ options, $value, isMulti, ...rest }: { options: any, $value: any, isMulti?: boolean }) => {
    const dataQaId = useQaId(rest['data-qa-id'] || 'digi-select');
    const ariaLabel: string = rest['aria-label'] || dataQaId;

    return (
        <Select
            aria-label={ ariaLabel }
            isMulti={isMulti}
            styles={{ ...customStyles, ...currencyDropdownStyles }}
            placeholder={''}
            options={options}
            getOptionLabel={(e: any) => `${e.code} - ${e.name}`}
            value={options.filter(function (option) {
                return option.code === $value.value;
            })}
            getOptionValue={(e: any) => e.code}
            isOptionDisabled={(e: any) => e.disabled}
            onChange={(value: any) => $value.set(value.code)}
        />
    )
}
const currencyDropdownStyles = {
    control: provided => ({
        ...provided,
        minHeight: 30,
        height: 30,
        borderRadius: 2,
        borderColor: '#E3E9ED'
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: 30
    }),
    indicatorsContainer: provided => ({
        height: 30,
        paddingTop: 3
    }),
    input: provided => ({
        height: 30,
        marginTop: -3
    })
}

const customStyles = {
    container: provided => ({
        ...provided,
        width: '100%'
    }),
    control: provided => ({
        ...provided,
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#E3E9ED'
    }),
    valueContainer: (provided) => ({
        ...provided,
        width: 'auto',
        overflowX: 'hidden',
        overflowY: 'hidden',
        fontSize: 14,
        fontWeight: 500,
    }),
    indicatorSeparator: provided => ({
        display: 'none'
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: 'rgba(0, 0, 0, 0.54)',
        marginRight: 0,
        padding: 0
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menu: (provided) => ({ ...provided, zIndex: 9999, marginTop: 0 })
}

const dropdownForAgGridStyles = {
    container: provided => ({
        ...provided,
        width: '100%'
    }),
    control: provided => ({
        ...provided,
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#E3E9ED'
    }),
    valueContainer: (provided) => ({
        ...provided,
        width: 'auto',
        overflowX: 'hidden',
        overflowY: 'hidden',
        fontSize: 14,
        fontWeight: 500,
        zIndex: 9999
    }),
    indicatorSeparator: provided => ({
        display: 'none'
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: 'rgba(0, 0, 0, 0.54)',
        marginRight: 0,
        padding: 0
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menu: (provided) => ({ 
        ...provided, 
        zIndex: 9999, 
        position: 'fixed', 
        width:'200px', marginTop: 0 })
}
